"use client"

import { useEffect, useRef, useState, type ComponentType } from "react"
import type { Platform } from "./SmartAppBannerDrawer"

type DrawerComponent = ComponentType<{ open: boolean; onOpenChange: (open: boolean) => void; platform: Platform }>

// The drawer (and vaul) is fetched on first intent, not with every landing page.
// This module owns the import() promise (no next/dynamic / React.lazy) so a failed chunk
// fetch is simply retried on the next tap instead of being cached as a rejection.
let loadedDrawer: DrawerComponent | null = null
let drawerRequest: Promise<DrawerComponent> | null = null

function loadDrawer(): Promise<DrawerComponent> {
	if (loadedDrawer) return Promise.resolve(loadedDrawer)
	if (!drawerRequest) {
		drawerRequest = import("./SmartAppBannerDrawer")
			.then((m) => {
				loadedDrawer = m.default
				return m.default
			})
			.catch((error) => {
				drawerRequest = null
				throw error
			})
	}
	return drawerRequest
}

function isBannerOpenButton(target: EventTarget | null): boolean {
	return target instanceof Element && target.closest("#smart-banner-open") !== null
}

export default function SmartAppBannerModal() {
	const [isOpen, setIsOpen] = useState(false)
	const [platform, setPlatform] = useState<Platform>("ios")
	const [Drawer, setDrawer] = useState<DrawerComponent | null>(() => loadedDrawer)
	const openAfterMountRef = useRef(false)
	const drawerMountedRef = useRef(false)

	// First open: the drawer has just mounted closed; open it on the next frame so vaul plays its slide-in.
	useEffect(() => {
		drawerMountedRef.current = Drawer !== null
		if (!Drawer || !openAfterMountRef.current) return
		openAfterMountRef.current = false
		const frame = requestAnimationFrame(() => setIsOpen(true))
		return () => cancelAnimationFrame(frame)
	}, [Drawer])

	useEffect(() => {
		// One delegated listener instead of a body-wide MutationObserver waiting for the
		// banner's #smart-banner-open button (that observer ran on every DOM change of every
		// landing page, forever on desktop where the banner never renders).
		const preload = (event: Event) => {
			if (isBannerOpenButton(event.target)) loadDrawer().catch(() => {})
		}

		const handler = (event: MouseEvent) => {
			if (!isBannerOpenButton(event.target)) return

			// Android: try native install prompt first
			const deferred = (window as any).__deferredInstallPrompt
			if (deferred) {
				deferred.prompt()
				return
			}
			// iOS / fallback: show instructions drawer
			const ua = navigator.userAgent
			const isIos = /iPhone|iPod/.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua))
			setPlatform(isIos ? "ios" : "android")

			if (drawerMountedRef.current) {
				setIsOpen(true)
				return
			}
			loadDrawer()
				.then((component) => {
					openAfterMountRef.current = true
					setDrawer(() => component)
				})
				.catch(() => {})
		}

		document.addEventListener("pointerdown", preload, { passive: true })
		document.addEventListener("click", handler)
		return () => {
			document.removeEventListener("pointerdown", preload)
			document.removeEventListener("click", handler)
		}
	}, [])

	if (!Drawer) return null
	return <Drawer open={isOpen} onOpenChange={setIsOpen} platform={platform} />
}
